import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send, Clock } from "lucide-react";

const NotesHistoryModal = ({ open, onClose, jobCard }) => {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && jobCard) {
      fetchComments();
    }
  }, [open, jobCard]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobcards/${jobCard.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to fetch comments");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/jobcards/${jobCard.id}/comments`, { comment: comment.trim() });
      toast.success("Comment added successfully");
      setComment("");
      fetchComments();
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!jobCard) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="notes-history-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <MessageSquare className="w-5 h-5 text-blue-600" />
            {jobCard.job_card_number} - Notes
          </DialogTitle>
          <DialogDescription>
            {jobCard.customer_name} &middot; {jobCard.type_of_service}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex gap-2 mt-2">
          <Textarea
            data-testid="add-comment-input"
            placeholder="Add a note or comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="flex-1 resize-none"
          />
          <Button
            data-testid="submit-comment-button"
            type="submit"
            disabled={submitting || !comment.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white self-end px-4"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>

        <div className="flex-1 overflow-y-auto mt-4 space-y-3 min-h-[200px] max-h-[400px]">
          {loading ? (
            <div className="text-center text-slate-500 py-8">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No comments yet. Add the first note.</p>
            </div>
          ) : (
            [...comments].reverse().map((c, idx) => (
              <div
                key={c.date || `comment-${idx}`}
                data-testid={`comment-item-${idx}`}
                className="bg-slate-50 rounded-lg p-3 border border-slate-100"
              >
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{c.comment}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(c.date)}</span>
                  {c.added_by && (
                    <>
                      <span>&middot;</span>
                      <span className="font-medium">{c.added_by}</span>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotesHistoryModal;
